/**
 * The HTTP client.
 *
 * Everything the app knows about the server goes through here, so there is one
 * place that understands the error envelope, one place that carries the session
 * cookie, and one place to look when a request behaves oddly.
 *
 * Requests are same-origin in both dev and production — Vite proxies `/api` to
 * the local Worker — so the session cookie is sent without CORS credentials
 * juggling, and a `SameSite=Lax` cookie behaves identically in both.
 */

/** The error envelope the API guarantees. See `api/src/lib/errors.ts`. */
export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** A 401 means the session is gone; the app routes to sign-in rather than
   *  showing an error, because "you were logged out" is not an error state. */
  get isAuth(): boolean {
    return this.status === 401;
  }
}

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

interface RequestOptions {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
  /** Query parameters. Undefined and null values are dropped, not stringified
   *  into the literal text "undefined" — which is a classic silent bug. */
  query?: Record<string, string | number | boolean | undefined | null>;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = `/api${path.startsWith("/") ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * Fixture mode. `VITE_FIXTURES=1` resolves every read from `lib/fixtures.ts`
 * instead of the network, so a screen can be built, screenshotted and
 * criticised before its endpoint exists. The guard is a build-time constant,
 * so a production bundle tree-shakes the fixtures out entirely.
 *
 * Writes still "succeed" and return their own body, which is enough for an
 * optimistic update to be exercised visually. It is not a mock server and it
 * does not pretend to be one.
 */
const USE_FIXTURES = import.meta.env.VITE_FIXTURES === "1";

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, signal, query } = options;

  if (USE_FIXTURES) {
    const { fixtureFor } = await import("./fixtures");
    // A short delay so loading states are actually visible while building
    // them; without it every screen renders instantly and skeletons go
    // untested until a real network shows up.
    await new Promise((r) => setTimeout(r, 120));
    if (method !== "GET") return (body ?? {}) as T;
    return fixtureFor(path) as T;
  }

  const res = await fetch(buildUrl(path, query), {
    method,
    signal,
    headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    // The session lives in an HttpOnly cookie; nothing here ever handles a
    // token directly, which is why an XSS cannot exfiltrate a session.
    credentials: "same-origin",
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const envelope = parsed as Partial<ApiErrorBody> | undefined;
    throw new ApiError(
      res.status,
      envelope?.error?.code ?? "unknown",
      envelope?.error?.message ?? `Request failed (${res.status})`,
      envelope?.error?.details,
    );
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<T>(path, { method: "GET", query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
